import { JsonPostQueryHelper } from "../utils";
import User from '../users-management/user';
import Customer from "../customers-management/customer";
import { StoryElement } from "../storyelements-management/story-element";

export interface GmailConnectionStatus {
    connected: boolean;
    email?: string;
}

export interface GmailDraftResult {
    customer: string;
    fileName: string;
    draftId: string;
    messageId?: string;
}

export interface GmailBulkDraftResult extends Partial<GmailDraftResult> {
    ok: boolean;
    customer: string;
    error?: string;
}

export default class StorylineServerProxy {

    static LOCALHOST_URL_BASE = "http://localhost:45828/api/v1";
    static TEST_SERVER_URL_BASE = "https://test-storyline-2.foxatm.com/api/v1";
    static OPS_SERVER_URL_BASE = "https://ops-storyline.foxatm.com/api/v1";

    static setURLBase(url: string){
        JsonPostQueryHelper.setUrlBase(url); 
    }

    static getURLBase(): string{
        return JsonPostQueryHelper.getUrlBase();
    }

    static authenticate = (username: string, password: string) => JsonPostQueryHelper.sendRequestNoToken("/authenticate", { username: username, password: password} );

    static setToken = (token: string) => JsonPostQueryHelper.setToken(token);

    static getToken = () => JsonPostQueryHelper.getToken();

    static changePassword(username: string, oldPassword: string, newPassword: string){
        let requestBody = {
            username: username,
            oldPassword: oldPassword,
            newPassword: newPassword
        }
  
      return JsonPostQueryHelper.sendRequest("/changePassword", requestBody);
    }

    static getUser = (username: string) => JsonPostQueryHelper.sendRequest("/user", { username: username }, false);
    static getUsers = () => JsonPostQueryHelper.sendRequest("/users", {}, false);
    static updateUser = (user: User) => JsonPostQueryHelper.sendRequest("/updateUser", { user: user }, false);

    static createCustomer = (customer: Customer) => JsonPostQueryHelper.sendRequest("/customer/create", { customer: customer});
    static getCustomers = () => JsonPostQueryHelper.sendRequest("/customers", {});
    static updateCustomer = (customer: Customer) => JsonPostQueryHelper.sendRequest("/customer/update", { customer: customer});
    static deleteCustomer = (customerName: string) => JsonPostQueryHelper.sendRequest("/customer/delete", { customerName: customerName} );
    
    static reportForCustomer = (customer: string, firstDate: string, lastDate: string) => JsonPostQueryHelper.sendRequest("/report/reportForCustomer", { customer: customer, firstDate: firstDate, lastDate: lastDate} );
    static reportsForLead = (lead: string) => JsonPostQueryHelper.sendRequest("/report/reportsForLead", { lead: lead} );
    static multiStorylinesReport = (storylines: string[], firstDate: string, lastDate: string) => JsonPostQueryHelper.sendRequest("/report/multiStorylinesReport", { storylines: storylines, firstDate: firstDate, lastDate: lastDate} );

    static gmailStatus = () => JsonPostQueryHelper.sendRequestChecked<GmailConnectionStatus>("/gmail/status", {}, false);
    static startGmailOAuth = () => JsonPostQueryHelper.sendRequestChecked<{ authorizationUrl: string }>("/gmail/oauth/start", {}, false);
    static disconnectGmail = () => JsonPostQueryHelper.sendRequestChecked<GmailConnectionStatus>("/gmail/disconnect", {}, false);
    static createGmailDraft = (customer: string, firstDate: string, lastDate: string) => JsonPostQueryHelper.sendRequestChecked<GmailDraftResult>("/gmail/draft/create", { customer: customer, firstDate: firstDate, lastDate: lastDate });
    static createGmailDrafts = (customerNames: string[]) => JsonPostQueryHelper.sendRequestChecked<{ results: GmailBulkDraftResult[] }>("/gmail/drafts/create", { customerNames: customerNames });

    static getScrapers = () => JsonPostQueryHelper.sendRequest("/scrapers", {});
    static createScraper = (scraper: any) => JsonPostQueryHelper.sendRequest("/scraper/create", { scraper: scraper });
    static updateScraper = (scraper: any) => JsonPostQueryHelper.sendRequest("/scraper/update", { scraper: scraper});
    static deleteScraper = (name: any) => JsonPostQueryHelper.sendRequest("/scraper/delete", { name: name});
    static reprocessLastScrap = (scraperId: any) => JsonPostQueryHelper.sendRequest("/scraper/reprocessLastScrap", { scraperId: scraperId});

    static runScrapers = (forceRun: boolean, headless: boolean) => JsonPostQueryHelper.sendRequest("/scrapers/run", { forceRun: forceRun, headless: headless });
    static runOneScraper = (scraper: any, headless: boolean) => JsonPostQueryHelper.sendRequest("/scrapers/runOne", { scraper: scraper, headless: headless });
    static isScrapingRunning = () => JsonPostQueryHelper.sendRequest("/scrapers/isRunning", {});
    static scrapingStatus = () => JsonPostQueryHelper.sendRequest("/scrapers/runStatus", {});
    static abortScraping = () => JsonPostQueryHelper.sendRequest("/scrapers/abort", {});;

    static getFailedScrapReports = (pageSize: number, pageNumber: number) => JsonPostQueryHelper.sendRequest("/failedScrapReports", { pageSize: pageSize, pageNumber: pageNumber});
    static getFailedScrapReportsCount = () => JsonPostQueryHelper.sendRequest("/failedScrapReports/count", {});
    static markAllFailedAsDeleted = () => JsonPostQueryHelper.sendRequest("/failedScrapReports/deleteAll", {});

    static getScrapReports = (pageSize: number, pageNumber: number) => JsonPostQueryHelper.sendRequest("/scrapReports", { pageSize: pageSize, pageNumber: pageNumber});
    static getScrapReportsCount = () => JsonPostQueryHelper.sendRequest("/scrapReports/count", {});
    static getDeletedScrapReports = (pageSize: number, pageNumber: number, pattern: string) => JsonPostQueryHelper.sendRequest("/scrapReports/deleted", { pageSize: pageSize, pageNumber: pageNumber, pattern: pattern});
    static getDeletedScrapReportsCount = (pattern: string) => JsonPostQueryHelper.sendRequest("/scrapReports/deletedCount", {pattern: pattern});
    static markScrapReportAsDeleted = (id: string) => JsonPostQueryHelper.sendRequest("/scrapReport/markAsDeleted", { id: id});
    static markAllScrapReportsAsDeleted = () => JsonPostQueryHelper.sendRequest("/scrapReports/markAllAsDeleted", { });
    static restoreScrapReport = (id: string) => JsonPostQueryHelper.sendRequest("/scrapReport/restore", { id: id });
    static analyseScrapReport = (id: string) => JsonPostQueryHelper.sendRequest("/scrapReport/analyse", { id: id });
    
    static getStoryElements = (pageSize: number, pageNumber: number) => JsonPostQueryHelper.sendRequest("/storyElements", {pageSize: pageSize, pageNumber: pageNumber}) as Promise< StoryElement[] >;
    static getFilteredStoryElements = (filter: any, pageSize: number, pageNumber: number) => JsonPostQueryHelper.sendRequest("/storyElements/filtered", {filter: filter, pageSize: pageSize, pageNumber: pageNumber}) as Promise< StoryElement[] >;
    static getJobs = (pageSize: number, pageNumber: number) => JsonPostQueryHelper.sendRequest("/storyElements/jobs", {pageSize: pageSize, pageNumber: pageNumber}) as Promise< StoryElement[] >;
    static getUnarchivedStoryElements = () => JsonPostQueryHelper.sendRequest("/storyElements/unarchived", {}) as Promise< StoryElement[] >;
    static getStoryElementsCount = () => JsonPostQueryHelper.sendRequest("/storyElements/count", {}) as Promise<{ count: number }>;
    static getStoryElementsForWeeklyReview = () => JsonPostQueryHelper.sendRequest("/storyElements/weeklyReview", {});
    static getStoryElementsForSocialMedia = () => JsonPostQueryHelper.sendRequest("/storyElements/socialMedia", {});
    static getStoryElementsForStudents = () => JsonPostQueryHelper.sendRequest("/storyElements/students", {});
    static resetWeeklyAndStudents = () => JsonPostQueryHelper.sendRequest("/storyElements/resetWeeklyAndStudents", {});
    static createStoryElement = (storyElement: any) => JsonPostQueryHelper.sendRequest("/storyElement/create", { storyElement: storyElement });
    static updateStoryElement = (storyElement: any) => JsonPostQueryHelper.sendRequest("/storyElement/update", { storyElement: storyElement });
    static checkLinksUnicity = (links: string[]) => JsonPostQueryHelper.sendRequest("/storyElements/checkUnicity", { links: links });
    static getLinkTitleAndDescription = (link: string) => JsonPostQueryHelper.sendRequest("/storyElement/getLinkTitleAndDescription", { link: link });
    static getLinkTitleDescriptionSummary = (link: string) => JsonPostQueryHelper.sendRequest("/storyElement/getLinkTitleDescriptionSummary", { link: link });
    static deleteStoryElement = (storyElementId: any) => JsonPostQueryHelper.sendRequest("/storyElement/delete", { id: storyElementId});
    static archiveStoryElement = (storyElement: any) => JsonPostQueryHelper.sendRequest("/storyElement/archiveElementWithId", { id: storyElement._id});
    static noticeForDate = (year: number, month: number, day: number) => JsonPostQueryHelper.sendRequest("/TEDs/noticeForDate", { year: year, month: month, day: day });
    static downloadForDate = (year: number, month: number, day: number) => JsonPostQueryHelper.sendRequest("/TEDs/downloadForDate", { year: year, month: month, day: day });
    static processForDate = (year: number, month: number, day: number) => JsonPostQueryHelper.sendRequest("/TEDs/processForDate", { year: year, month: month, day: day });

    static pendingTEDs = () => JsonPostQueryHelper.sendRequest("/TEDs/pending", {});
    static processedTEDs = () => JsonPostQueryHelper.sendRequest("/TEDs/processed", {});
    static deletePendingTED = (ted: any) => JsonPostQueryHelper.sendRequest("/TEDs/deletePending", { TED: ted });
    static deleteProcessedTED = (ted: any) => JsonPostQueryHelper.sendRequest("/TEDs/deleteProcessed", { TED: ted });
    static deleteAllProcessedTED = () => JsonPostQueryHelper.sendRequest("/TEDs/deleteAllProcessed", {});

    static allTEDFilters = () => JsonPostQueryHelper.sendRequest("/TEDFilters/all", {});
    static updateTEDFilter = (filter: any) => JsonPostQueryHelper.sendRequest("/TEDFilter/update", {filter: filter});
    static addTEDFilter = (filter: any) => JsonPostQueryHelper.sendRequest("/TEDFilter/add", {filter: filter});
    static deleteTEDFilter = (filter: any) => JsonPostQueryHelper.sendRequest("/TEDFilter/delete", {filter: filter});

    static allRobotsStatus = () => JsonPostQueryHelper.sendRequest("/robots", {}, false);

    static storeDatabaseBackup = () => JsonPostQueryHelper.sendRequest("/backup/create", { destination: "local" });
    static downloadDatabaseBackup = () => JsonPostQueryHelper.sendDownloadRequest("/backup/create", { destination: "download" });
    static databaseBackupStatus = () => JsonPostQueryHelper.sendRequest("/backup/status", {});

    static allExtraLinks = () => JsonPostQueryHelper.sendRequest("/extraLinks/all", {});
    static addExtraLink = (extraLink: any) => JsonPostQueryHelper.sendRequest("/extraLinks/create", {extraLink: extraLink});
    static updateExtraLink = (extraLink: any) => JsonPostQueryHelper.sendRequest("/extraLinks/update", {extraLink: extraLink});
    static deleteExtraLink = (link: string) => JsonPostQueryHelper.sendRequest("/extraLinks/delete", {link: link});

    static search = (keywords: string, category: string) => JsonPostQueryHelper.sendRequest("/search", { keywords: keywords, category: category })
}

StorylineServerProxy.setURLBase(StorylineServerProxy.LOCALHOST_URL_BASE);
